import { Button } from '@tvg/fuse/button';
import { Drawer as FuseDrawer } from '@tvg/fuse/drawer';
import { IconChevronRight } from '@tvg/fuse/icon/all';
import classNames from 'classnames';
import { PLPQueryData, usePLPQuery } from 'client/api/plp-results';
import { EUserAction } from 'client/config';
import useAutoRef from 'client/hooks/useAutoRef';
import { useMainDrawer } from 'client/hooks/useMainDrawer';
import { usePLPNavigate } from 'client/hooks/usePLPNavigate';
import { useDrawerState } from 'client/state/Drawer';
import { FILTER_DRAWER_BUTTON_ID } from 'client/utils/consts';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import FacetItemList from '../Facet/FacetItemList';
import styles from './Drawer.module.css';

const handleCloseCallback = () => {};

const cssZIndex = '20'; // should be came from FUSE, we add it because the DCX Header will overlay our Drawer component

export function Drawer() {
  const { drawerOpen, showViewProducts, ariaHidden, toggleAriaHidden, toggleDrawerOpen, toggleShowViewProducts } =
    useDrawerState();
  const { data } = usePLPQuery(
    useCallback((d: PLPQueryData) => {
      const requestedParentId = d.meta.request?.filters?.group_id?.[0];
      const parent = d.parentCategories.at(-1);
      return {
        categoryName: d.category.display_name?.toLowerCase() === 'all' ? undefined : d.category.display_name,
        parentCategoryId:
          !parent || parent.group_id !== requestedParentId ? parent?.group_id : d.parentCategories.at(-2)?.group_id,
      };
    }, []),
  );
  const plpNavigate = usePLPNavigate();

  const drawerRef = useRef<HTMLDivElement>(null);
  const topCategoryGroupId = useRef(data?.parentCategoryId);
  const parentCategoryIdRef = useAutoRef(data?.parentCategoryId);

  const Portal = useMainDrawer(drawerOpen);

  const hasBackButton = useMemo(
    () => Boolean(data?.parentCategoryId !== topCategoryGroupId.current && data?.categoryName?.length),
    [data?.categoryName, data?.parentCategoryId],
  );

  useEffect(() => {
    if (!drawerOpen) {
      toggleAriaHidden.on();
      toggleShowViewProducts.off();
    } else {
      setTimeout(() => drawerRef.current?.focus(), 100);
    }
  }, [drawerOpen, toggleAriaHidden, toggleShowViewProducts]);

  useEffect(() => {
    if (!ariaHidden) {
      drawerRef.current?.focus();
    }
  }, [ariaHidden]);

  const onClose = useCallback(() => {
    toggleAriaHidden.off();
    toggleDrawerOpen.off();
    toggleShowViewProducts.off();
    setTimeout(() => {
      const filterButton = document.getElementById(FILTER_DRAWER_BUTTON_ID);
      filterButton?.focus();
    }, 100);
  }, [toggleAriaHidden, toggleDrawerOpen, toggleShowViewProducts]);

  const backCallback = useCallback(() => {
    // Fetch the parent category unless we are at the root category (1 element)
    plpNavigate(
      {
        category:
          !parentCategoryIdRef.current || parentCategoryIdRef.current === 'all' ? false : parentCategoryIdRef.current,
      },
      EUserAction.CATEGORY,
    );
  }, [parentCategoryIdRef, plpNavigate]);

  const stickyDrawer = useMemo(
    () => ({
      container: {
        className: 'fuse-drawer__sticky-drawer',
        dataTestId: 'fuse-drawer-sticky-drawer-data-testid',
        open: showViewProducts,
        transition: '200ms',
        zIndex: '1',
      },
      button: (
        <Button
          className="fuse-drawer__sticky-drawer-button"
          dataTestId="fuse-drawer-sticky-drawer-button-data-testid"
          fullWidth
          icon={<IconChevronRight />}
          iconRight
          size="large"
          variant="primary"
          onClick={onClose}
        >
          View Products
        </Button>
      ),
    }),
    [onClose, showViewProducts],
  );

  return (
    <Portal>
      <>
        <div
          className={classNames(styles.drawer, 'drawer__first-level mobile-filters')}
          aria-hidden={!drawerOpen || ariaHidden}
        >
          <FuseDrawer
            srLabelBackButton="Previous filters"
            srLabelCloseButton="Close filter list"
            className="modal mobile-filters-drawer"
            hasBackButton={hasBackButton}
            open={drawerOpen}
            onClose={onClose}
            title={data?.categoryName || 'Filter'}
            backCallback={backCallback}
            closeCallback={handleCloseCallback}
            zIndex={cssZIndex}
            dataTestId="facet-drawer"
            ref={drawerRef}
            stickyDrawer={stickyDrawer}
          >
            <FacetItemList isDrawerView />
          </FuseDrawer>
        </div>
        <div className={styles.drawer__innerPlaceholder} id="drawer--inner" />
      </>
    </Portal>
  );
}
